import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import IControlBarTogglesEnabledState from "../../services/playbackOrchestrator/IControlBarTogglesEnabledState";
import ISelectOption from "../../models/view/common/ISelectOption";
import DemoMode from "../../models/view/lessonPage/DemoMode";
import Lesson from "../../models/view/lessonPage/Lesson";
import LessonSection from "../../models/view/lessonPage/LessonSection";
import IScoreModal from "../../models/view/lessonPage/IScoreModal";
import Segment from "../../models/view/lessonPage/Segment";
import {appPagePaths} from "../../config/config";
import {
  getKeyboardVisibilityPropertyAssociatedWithSegmentMode
} from "../../models/view/lessonPage/LessonPlayerSettings";
import {NotationLayoutEnum} from "../../models/view/lessonPage/NotationLayoutEnum";
import {arrayUpsert} from "../../util/array-util";
import {
  createPlaybackControlState
} from "../../services/playbackOrchestrator/PlaybackControlState/PlaybackControlStateFactory";
import {
  ILessonSpacing,
  IPlaybackControlState
} from "../../services/playbackOrchestrator/PlaybackControlState/IPlaybackControlStateTypes";

const defaultDemoModeSelectOptions: ISelectOption<DemoMode>[] = [
  {
    label: 'Right Hand',
    value: 'right hand'
  },
  {
    label: 'Left Hand',
    value: 'left hand'
  },
  {
    label: 'Both Hands',
    value: 'both hands'
  },
  {
    label: 'Highlights Off',
    value: 'off'
  }
];

export type ILessonPlayerStore = {
  activeLessonId: number | undefined,
  backButtonUrl: string,
  lastTensorFlowBackendEngineInitialized: string,
  canShowDemoSoundActivatedPrompt: boolean,
  canShowKeyboardConnectedModal: boolean,
  canShowKeyboardDisconnectedModal: boolean,
  canShowPitchDetectionDisabledDueToDemoSoundEnabledMessage: boolean,
  canShowSwitchToPitchDetectionPrompt: boolean,
  controlBarTogglesEnabledState: IControlBarTogglesEnabledState,
  demoModeSelectOptions: ISelectOption<DemoMode>[],
  demoSoundPrompt: {
    isAllowedByUser: boolean,
  },
  displayLessonEndsWithVideoModal: boolean,
  displayNewSectionModalForLesson?: number,
  displayScoreModal: boolean,
  displaySectionEndModal: boolean,
  displaySectionStartModal: boolean,
  isControlBarPartiallyHidden: boolean,
  isCurrentMeasureHighlighted: boolean,
  isDemoModeDropdownSelectorOpen: boolean,
  isDemoSoundActivatedPromptAllowedByUser: boolean,
  isFirstLessonOfSection: boolean,
  isLastLessonOfSection: boolean,
  isLessonLoading: boolean,
  isMidiConnected: boolean,
  isProgressBarShown: boolean,
  isSegmentDropdownSelectorOpen: boolean,
  isSettingsDropdownSelectorOpen: boolean,
  isSwitchToPitchDetectionPromptAllowedByUser: boolean,
  isTempoDropdownSelectorOpen: boolean,
  lesson?: Lesson,
  metrics: {
    segmentsOpenedCount: number, // Tracks how many times a segment has been opened
  }
  nextSection?: LessonSection,
  playbackControlState: IPlaybackControlState,
  recordingStartTimeInMs: number | null,
  scoreModalViewModel?: IScoreModal,
  scoringStartTimeInMs: number | null,
  section?: LessonSection,
  segment?: Segment,
  showPermissionDeniedModal: boolean,
  shouldPlayWhenSSReady: boolean,
  transcriptionProgressPercentage: string,
  transcriptionStatus: "processing" | "completed",
}

const initialState: ILessonPlayerStore = {
  activeLessonId: undefined,
  backButtonUrl: appPagePaths.bootCampPage,
  lastTensorFlowBackendEngineInitialized: "",
  canShowDemoSoundActivatedPrompt: false,
  canShowKeyboardConnectedModal: false,
  canShowKeyboardDisconnectedModal: false,
  canShowPitchDetectionDisabledDueToDemoSoundEnabledMessage: false,
  canShowSwitchToPitchDetectionPrompt: false,
  controlBarTogglesEnabledState: {
    isBackingTrackToggleEnabled: true,
    isCountInToggleEnabled: true,
    isCurrentMeasureHighlightToggleEnabled: true,
    isDemoSelectorEnabled: true,
    isFingerNumbersToggleEnabled: true,
    isInstantFeedbackToggleEnabled: true,
    isKeyboardToggleEnabled: true,
    isLoopButtonEnabled: true,
    isMetronomeToggleEnabled: true,
    isNotationLayoutToggleEnabled: true,
    isNoteNamesToggleEnabled: true,
    isPlayheadToggleEnabled: true,
    isProportionalToggleEnabled: true,
    isRhythmCountsToggleEnabled: true,
    isScrollDirectionToggleEnabled: true,
    isTempoSelectorEnabled: true,
    isZoomButtonEnabled: true,
  },
  demoModeSelectOptions: defaultDemoModeSelectOptions,
  demoSoundPrompt: {
    isAllowedByUser: true,
  },
  displayLessonEndsWithVideoModal: false,
  displayNewSectionModalForLesson: undefined,
  displayScoreModal: false,
  displaySectionEndModal: false,
  displaySectionStartModal: false,
  isControlBarPartiallyHidden: false,
  isCurrentMeasureHighlighted: false,
  isDemoModeDropdownSelectorOpen: false,
  isDemoSoundActivatedPromptAllowedByUser: false,
  isFirstLessonOfSection: false,
  isLastLessonOfSection: false,
  isLessonLoading: true,
  isMidiConnected: false,
  isProgressBarShown: true,
  isSegmentDropdownSelectorOpen: false,
  isSettingsDropdownSelectorOpen: false,
  isSwitchToPitchDetectionPromptAllowedByUser: false,
  isTempoDropdownSelectorOpen: false,
  lesson: undefined,
  metrics: {
    segmentsOpenedCount: 0,
  },
  nextSection: undefined,
  playbackControlState: createPlaybackControlState(),
  recordingStartTimeInMs: null,
  scoreModalViewModel: undefined,
  scoringStartTimeInMs: null,
  section: undefined,
  segment: undefined,
  showPermissionDeniedModal: false,
  shouldPlayWhenSSReady: false,
  transcriptionProgressPercentage: '100',
  transcriptionStatus: "completed", // Depends on the transcriptionProgressPercentage
}

export const lessonPlayerSlice = createSlice({
  name: 'lessonPlayer',
  initialState,

  reducers: (create) => ({
    /* NOTE: objects listening to changes made by reducers are not notified if the property being modified is not a direct child of the state object
      So if in a reducer I modify the state like this

      state.isLessonLoading = action.payload.isLessonLoading;

      then listeners will get notified. But if I modify the state like this

      state.playbackControlState.isLoopEnabled = action.playbackControlState.isLoopEnabled;

      then listeners will not get notified. So if you have a nested object that you need to modify, you should create a new object
      with the new properties and assign it to the state like this so listeners get notified

      state.playbackControlState = {...state.playbackControlState, isLoopEnabled: action.playbackControlState.isLoopEnabled};

      COMMENT FROM BEN: Tip: Behavior is likely the result of something we are doing at a fundamental level, probably near where all the slices are imported
      into the root store, since immer does support nested state updates out of the box.
     */

    appliedNotationLayoutReducer: create.reducer((state, action: PayloadAction<{ layout: NotationLayoutEnum }>) => {
      state.playbackControlState = {...state.playbackControlState, appliedNotationLayout: action.payload.layout};
    }),

    canShowDemoSoundActivatedPromptReducer: create.reducer((state, action: PayloadAction<{
      canShow: boolean
    }>) => {
      state.canShowDemoSoundActivatedPrompt = action.payload.canShow;
    }),

    canShowKeyboardConnectedModalReducer: create.reducer((state, action: PayloadAction<{
      canShowKeyboardConnectedModal: boolean
    }>) => {
      state.canShowKeyboardConnectedModal = action.payload.canShowKeyboardConnectedModal;
    }),
    canShowKeyboardDisconnectedModalReducer: create.reducer((state, action: PayloadAction<{
      canShowKeyboardDisconnectedModal: boolean
    }>) => {
      state.canShowKeyboardDisconnectedModal = action.payload.canShowKeyboardDisconnectedModal;
    }),
    canShowPitchDetectionDisabledDueToDemoSoundEnabledMessageReducer: create.reducer((state, action: PayloadAction<{
      canShowPitchDetectionDisabledDueToDemoSoundEnabledMessage: boolean
    }>) => {
      state.canShowPitchDetectionDisabledDueToDemoSoundEnabledMessage = action.payload.canShowPitchDetectionDisabledDueToDemoSoundEnabledMessage;
    }),
    canShowSwitchToPitchDetectionPromptReducer: create.reducer((state, action: PayloadAction<{
      canShow: boolean
    }>) => {
      state.canShowSwitchToPitchDetectionPrompt = action.payload.canShow;
    }),
    zoomReducer: create.reducer((state, action: PayloadAction<{zoom: number}>) => {
      state.playbackControlState = {...state.playbackControlState, zoom: action.payload.zoom};
    }),
    demoModeReducer: create.reducer((state, action: PayloadAction<{demoMode: DemoMode}>) => {
      state.playbackControlState = {...state.playbackControlState, demoMode: action.payload.demoMode};
    }),
    chordNamesVisibleReducer: create.reducer((state, action: PayloadAction<{ isVisible: boolean }>) => {
      if (state.segment) {
        state.segment = {
          ...state.segment,
          lessonPlayerSettings: {...state.segment.lessonPlayerSettings, chordNamesVisible: action.payload.isVisible}
        };
      }
    }),
    controlBarTogglesEnabledStateReducer: create.reducer((state, action: PayloadAction<{
      controlBarTogglesEnabledState: IControlBarTogglesEnabledState
    }>) => {
      state.controlBarTogglesEnabledState = action.payload.controlBarTogglesEnabledState;
    }),
    displayRhythmCountsReducer: create.reducer((state, action: PayloadAction<{ isVisible: boolean }>) => {
      if (state.segment) {
        state.segment = {
          ...state.segment,
          lessonPlayerSettings: {...state.segment.lessonPlayerSettings, displayRhythmCounts: action.payload.isVisible}
        };
      }
    }),
    fretboardVisibleReducer: create.reducer((state, action: PayloadAction<{ isVisible: boolean }>) => {
      if (state.segment) {
        const propertyInQuestion = getKeyboardVisibilityPropertyAssociatedWithSegmentMode(state.segment.mode);
        state.segment = {
          ...state.segment,
          lessonPlayerSettings: {
            ...state.segment.lessonPlayerSettings,
            keyboard: {...state.segment.lessonPlayerSettings.keyboard, [propertyInQuestion]: action.payload.isVisible}
          }
        };
      }
    }),
    displayScoreModalReducer: create.reducer((state, action: PayloadAction<{ displayScoreModal: boolean }>) => {
      state.displayScoreModal = action.payload.displayScoreModal;
    }),
    displayNewSectionModalForLessonReducer: create.reducer((state, action: PayloadAction<{
      displayNewSectionModalForLesson?: number
    }>) => {
      state.displayNewSectionModalForLesson = action.payload.displayNewSectionModalForLesson;
    }),
    displaySectionStartModalReducer: create.reducer((state, action: PayloadAction<{
      displaySectionStartModal: boolean
    }>) => {
      state.displaySectionStartModal = action.payload.displaySectionStartModal;
    }),
    displaySectionEndModalReducer: create.reducer((state, action: PayloadAction<{
      displaySectionEndModal: boolean
    }>) => {
      state.displaySectionEndModal = action.payload.displaySectionEndModal;
    }),
    displayLessonEndsWithVideoModalReducer: create.reducer((state, action: PayloadAction<{
      displayLessonEndsWithVideoModal: boolean
    }>) => {
      state.displayLessonEndsWithVideoModal = action.payload.displayLessonEndsWithVideoModal;
    }),
    hidePlayheadWhilePlayingReducer: create.reducer((state, action: PayloadAction<{ hidePlayhead: boolean }>) => {
      state.playbackControlState = {
        ...state.playbackControlState,
        hidePlayheadDuringPlayback: action.payload.hidePlayhead
      };
    }),
    highlightCurrentMeasureReducer: create.reducer((state, action: PayloadAction<{
      highlightCurrentMeasure: boolean
    }>) => {
      state.playbackControlState = {
        ...state.playbackControlState,
        isCurrentMeasureHighlighted: action.payload.highlightCurrentMeasure
      };
    }),
    incrementMetricsScoreDisplayCountReducer: create.reducer((state) => {
      state.metrics = {...state.metrics, segmentsOpenedCount: state.metrics.segmentsOpenedCount + 1};
    }),
    isControlBarPartiallyHiddenReducer: create.reducer((state, action: PayloadAction<{
      isControlBarPartiallyHidden: boolean
    }>) => {
      state.isControlBarPartiallyHidden = action.payload.isControlBarPartiallyHidden;
    }),
    isCountInPlayingReducer: create.reducer((state, action: PayloadAction<{ isCountInPlaying: boolean }>) => {
      state.playbackControlState = {...state.playbackControlState, isCountInPlaying: action.payload.isCountInPlaying};
    }),
    isDemoLeftHandSoundEnabledReducer: create.reducer((state, action: PayloadAction<{isDemoLeftHandSoundEnabled: boolean}>) => {
      state.playbackControlState = {
        ...state.playbackControlState,
        isDemoLeftHandSoundEnabled: action.payload.isDemoLeftHandSoundEnabled
      };
    }),
    isDemoRightHandSoundEnabledReducer: create.reducer((state, action: PayloadAction<{isDemoRightHandSoundEnabled: boolean}>) => {
      state.playbackControlState = {
        ...state.playbackControlState,
        isDemoRightHandSoundEnabled: action.payload.isDemoRightHandSoundEnabled
      };
    }),
    isDemoSelectorDropdownOpenReducer: create.reducer((state, action: PayloadAction<{
      isDemoDropdownSelectorOpen: boolean
    }>) => {
      state.isDemoModeDropdownSelectorOpen = action.payload.isDemoDropdownSelectorOpen;
    }),

    isDemoSoundActivatedPromptAllowedByUserReducer: create.reducer((state, action: PayloadAction<{
      isAllowed: boolean
    }>) => {
      state.isDemoSoundActivatedPromptAllowedByUser = action.payload.isAllowed;
    }),

    isDemoSoundPromptAllowedByUserReducer: create.reducer((state, action: PayloadAction<{ isAllowed: boolean }>) => {
      state.demoSoundPrompt = {...state.demoSoundPrompt, isAllowedByUser: action.payload.isAllowed};
    }),
    isInstantFeedbackEnabledReducer: create.reducer((state, action: PayloadAction<{ isEnabled: boolean }>) => {
      state.playbackControlState = {
        ...state.playbackControlState,
        isInstantFeedbackEnabled: action.payload.isEnabled
      };
    }),
    isLessonLoadingReducer: create.reducer((state, action: PayloadAction<{ isLessonLoading: boolean }>) => {
      state.isLessonLoading = action.payload.isLessonLoading;
    }),
    isMidiConnectedReducer: create.reducer((state, action: PayloadAction<{ isConnected: boolean }>) => {
      state.isMidiConnected = action.payload.isConnected;
    }),
    isPlaybackEnabledReducer: create.reducer((state, action: PayloadAction<{ isPlaybackEnabled: boolean }>) => {
      state.playbackControlState = {...state.playbackControlState, isPlaybackEnabled: action.payload.isPlaybackEnabled};
    }),
    isPlayingReducer: create.reducer((state, action: PayloadAction<{ isPlaying: boolean }>) => {
      state.playbackControlState = {...state.playbackControlState, isPlaying: action.payload.isPlaying};
    }),
    isProgressBarShownReducer: create.reducer((state, action: PayloadAction<{ isProgressBarShown: boolean }>) => {
      state.isProgressBarShown = action.payload.isProgressBarShown;
    }),
    isSegmentSelectorDropdownOpenReducer: create.reducer((state, action: PayloadAction<{
      isSegmentDropdownSelectorOpen: boolean
    }>) => {
      state.isSegmentDropdownSelectorOpen = action.payload.isSegmentDropdownSelectorOpen;
    }),
    isSettingsSelectorDropdownOpenReducer: create.reducer((state, action: PayloadAction<{
      isSettingsSelectorDropdownOpen: boolean
    }>) => {
      state.isSettingsDropdownSelectorOpen = action.payload.isSettingsSelectorDropdownOpen;
    }),
    isSwitchToPitchDetectionPromptAllowedByUserReducer: create.reducer((state, action: PayloadAction<{
      isAllowed: boolean
    }>) => {
      state.isSwitchToPitchDetectionPromptAllowedByUser = action.payload.isAllowed;
    }),
    isTempoSelectorDropdownOpenReducer: create.reducer((state, action: PayloadAction<{
      isTempoSelectorDropdownOpen: boolean
    }>) => {
      state.isTempoDropdownSelectorOpen = action.payload.isTempoSelectorDropdownOpen;
    }),
    lastTensorFlowBackendEngineInitializedReducer: create.reducer((state, action: PayloadAction<{
      lastTensorFlowBackendEngineInitialized: string
    }>) => {
      state.lastTensorFlowBackendEngineInitialized = action.payload.lastTensorFlowBackendEngineInitialized;
    }),
    lessonLoadedReducer: create.reducer((state, action: PayloadAction<{ lesson?: Lesson }>) => {
      state.isLessonLoading = false;
      state.lesson = action.payload.lesson;
    }),
    lessonPlayerSettingsForceSideVideoReducer: create.reducer((state, action: PayloadAction<{
      forceSideVideo: boolean
    }>) => {
      if (state.segment) {
        state.segment = {
          ...state.segment,
          lessonPlayerSettings: {...state.segment.lessonPlayerSettings, forceSideVideo: action.payload.forceSideVideo}
        };
      }
    }),
    lessonPlayerSettingsForceTopVideoReducer: create.reducer((state, action: PayloadAction<{
      forceTopVideo: boolean
    }>) => {
      if (state.segment) {
        state.segment = {
          ...state.segment,
          lessonPlayerSettings: {...state.segment.lessonPlayerSettings, forceTopVideo: action.payload.forceTopVideo}
        };
      }
    }),
    loopEnabledReducer: create.reducer((state, action: PayloadAction<{ isLoopEnabled: boolean }>) => {
      state.playbackControlState = {...state.playbackControlState, isLoopEnabled: action.payload.isLoopEnabled};
    }),
    metricsScoreDisplayCountReducer: create.reducer((state, action: PayloadAction<{ count: number }>) => {
      state.metrics = {...state.metrics, segmentsOpenedCount: action.payload.count};
    }),
    noteNamesVisibleReducer: create.reducer((state, action: PayloadAction<{ isVisible: boolean }>) => {
      if (state.segment) {
        state.segment = {
          ...state.segment,
          lessonPlayerSettings: {...state.segment.lessonPlayerSettings, noteNamesVisible: action.payload.isVisible}
        };
      }
    }),
    pageCountReducer: create.reducer((state, action: PayloadAction<{ pageCount: number }>) => {
      state.playbackControlState = {...state.playbackControlState, pageCount: action.payload.pageCount};
    }),
    recordingStartTimeInMsReducer: create.reducer((state, action: PayloadAction<{
      recordingStartTimeInMs: number
    }>) => {
      state.recordingStartTimeInMs = action.payload.recordingStartTimeInMs;
    }),
    referrerReducer: create.reducer((state, action: PayloadAction<{ referrer: string }>) => {
      state.backButtonUrl = action.payload.referrer;
    }),
    scoreModalViewModelReducer: create.reducer((state, action: PayloadAction<{
      scoreModalViewModel?: IScoreModal
    }>) => {
      state.scoreModalViewModel = action.payload.scoreModalViewModel;
    }),
    scoringStartTimeInMsReducer: create.reducer((state, action: PayloadAction<{ scoringStartTimeInMs: number }>) => {
      state.scoringStartTimeInMs = action.payload.scoringStartTimeInMs;
    }),
    segmentReducer: create.reducer((state, action: PayloadAction<{ segment?: Segment }>) => {
      state.segment = action.payload.segment;
    }),
    segmentSoundSliceIFrameUrlReducer: create.reducer((state, action: PayloadAction<{ iframeUrl: string }>) => {
      if (state.segment) {
        state.segment = {...state.segment, soundsliceIframeUrl: action.payload.iframeUrl};
      }
    }),
    segmentsReducer: create.reducer((state, action: PayloadAction<{ segments: Segment[] }>) => {
      if (state.lesson) {
        state.lesson = {...state.lesson, segments: action.payload.segments};
      }
    }),
    selectedNotationLayoutReducer: create.reducer((state, action: PayloadAction<{ layout: NotationLayoutEnum }>) => {
      state.playbackControlState = {...state.playbackControlState, selectedNotationLayout: action.payload.layout};
    }),
    shouldHideSegmentDescriptionReducer: create.reducer((state, action: PayloadAction<{
      shouldHideSegmentDescription: boolean
    }>) => {
      if (state.segment) {
        state.segment = {...state.segment, shouldHideSegmentDescription: action.payload.shouldHideSegmentDescription};
      }
    }),
    showPermissionDeniedModalReducer: create.reducer((state, action: PayloadAction<{ show: boolean }>) => {
      state.showPermissionDeniedModal = action.payload.show;
    }),
    shouldPlayWhenSSReadyReducer: create.reducer((state, action: PayloadAction<{ shouldPlayWhenSSReady: boolean }>) => {
      state.shouldPlayWhenSSReady = action.payload.shouldPlayWhenSSReady;
    }),
    speedReducer: create.reducer((state, action: PayloadAction<{ speed: number }>) => {
      state.playbackControlState = {...state.playbackControlState, speed: action.payload.speed};
    }),
    staffFingeringsVisibleReducer: create.reducer((state, action: PayloadAction<{ isVisible: boolean }>) => {
      if (state.segment) {
        state.segment = {
          ...state.segment,
          lessonPlayerSettings: {
            ...state.segment.lessonPlayerSettings,
            staffFingeringsVisible: action.payload.isVisible
          }
        };
      }
    }),
    staffNotationVisibleReducer: create.reducer((state, action: PayloadAction<{ isVisible: boolean }>) => {
      if (state.segment) {
        state.segment = {
          ...state.segment,
          lessonPlayerSettings: {...state.segment.lessonPlayerSettings, staffNotationVisible: action.payload.isVisible}
        };
      }
    }),
    tempoReducer: create.reducer((state, action: PayloadAction<{ tempo: number }>) => {
      state.playbackControlState = {...state.playbackControlState, tempo: action.payload.tempo};
    }),
    transcriptionProgressPercentageReducer: create.reducer((state, action: PayloadAction<{ percent: string }>) => {
      state.transcriptionProgressPercentage = action.payload.percent;

      switch (action.payload.percent) {
        case '100':
          state.transcriptionStatus = "completed";
          break;
        default:
          state.transcriptionStatus = "processing";
          break;
      }
    }),

    //Notational spacing
    activeLessonIdReducer: create.reducer((state, action: PayloadAction<{lessonId: number | undefined}>) => {
      state.activeLessonId = action.payload.lessonId;
    }),
    allLessonSpacingReducer: create.reducer((state, action: PayloadAction<{lessonSpacing: ILessonSpacing[]}>) => {
      state.playbackControlState = {...state.playbackControlState, notationSpacing: {...state.playbackControlState.notationSpacing, lessonSpacing: action.payload.lessonSpacing}};
    }),
    lessonSpacingReducer: create.reducer((state, action: PayloadAction<{lessonId: number, spacing: number}>) => {
      const lessonSpacing = arrayUpsert({
        array: state.playbackControlState.notationSpacing.lessonSpacing,
        item: {lessonId: action.payload.lessonId, spacing: action.payload.spacing},
        compareFn: (a, b) => a.lessonId === b.lessonId
      });

      state.playbackControlState = {...state.playbackControlState, notationSpacing: {...state.playbackControlState.notationSpacing, lessonSpacing: lessonSpacing}};
    }),
    spacingForActiveLessonReducer: create.reducer((state, action: PayloadAction<{spacing: number | undefined}>) => {
      state.playbackControlState = {...state.playbackControlState, notationSpacing: {...state.playbackControlState.notationSpacing, spacingForActiveLesson: action.payload.spacing}};
    }),
  }),

  selectors: {
    activeLessonIdSelector: (state) => state.activeLessonId,
    appliedNotationLayoutSelector: (state) => state.playbackControlState.appliedNotationLayout,
    canShowDemoSoundActivatedPromptSelector: (state) => state.canShowDemoSoundActivatedPrompt,
    canShowKeyboardConnectedModalSelector: (state) => state.canShowKeyboardConnectedModal,
    canShowKeyboardDisconnectedModalSelector: (state) => state.canShowKeyboardDisconnectedModal,
    canShowSwitchToPitchDetectionPromptSelector: (state) => state.canShowSwitchToPitchDetectionPrompt,
    controlBarTogglesEnabledStateSelector: (state) => state.controlBarTogglesEnabledState,
    demoModeSelectOptionsSelector: (state) => state.demoModeSelectOptions,
    demoModeSelector: (state) => state.playbackControlState.demoMode,
    displayLessonEndsWithVideoModalSelector: (state) => state.displayLessonEndsWithVideoModal,
    displayNewSectionModalForLessonSelector: (state) => state.displayNewSectionModalForLesson,
    displayScoreModalSelector: (state) => state.displayScoreModal,
    displaySectionEndModalSelector: (state) => state.displaySectionEndModal,
    displaySectionStartModalSelector: (state) => state.displaySectionStartModal,
    hidePlayheadWhilePlayingSelector: (state) => state.playbackControlState.hidePlayheadDuringPlayback,
    highlightCurrentMeasureSelector: (state) => state.playbackControlState.isCurrentMeasureHighlighted,
    isControlBarPartiallyHiddenSelector: (state) => state.isControlBarPartiallyHidden,
    isCountInPlayingSelector: (state) => state.playbackControlState.isCountInPlaying,
    isDemoLeftHandSoundEnabledSelector: (state) => state.playbackControlState.isDemoLeftHandSoundEnabled,
    isDemoRightHandSoundEnabledSelector: (state) => state.playbackControlState.isDemoRightHandSoundEnabled,
    isDemoSelectorDropdownOpenSelector: (state) => state.isDemoModeDropdownSelectorOpen,
    isDemoSelectorEnabledSelector: (state) => state.controlBarTogglesEnabledState.isDemoSelectorEnabled,
    isDemoSoundActivatedPromptAllowedByUserSelector: (state) => state.isDemoSoundActivatedPromptAllowedByUser,
    isDemoSoundPromptAllowedByUserSelector: (state) => state.demoSoundPrompt.isAllowedByUser,
    isFirstLessonOfSectionSelector: (state) => state.isFirstLessonOfSection,
    isInstantFeedBackEnabledSelector: (state) => state.playbackControlState.isInstantFeedbackEnabled,
    isMidiConnectedSelector: (state) => state.isMidiConnected,
    isPlaybackEnabledSelector: (state) => state.playbackControlState.isPlaybackEnabled,
    isPlayingSelector: (state) => state.playbackControlState.isPlaying,
    isProgressBarShownSelector: (state) => state.isProgressBarShown,
    isSegmentSelectorDropdownOpenSelector: (state) => state.isSegmentDropdownSelectorOpen,
    isSettingsSelectorDropdownOpenSelector: (state) => state.isSettingsDropdownSelectorOpen,
    isSwitchToPitchDetectionPromptAllowedByUserSelector: (state) => state.isSwitchToPitchDetectionPromptAllowedByUser,
    isTempoSelectorDropdownOpenSelector: (state) => state.isTempoDropdownSelectorOpen,
    lastTensorFlowBackendEngineInitializedSelector: (state) => state.lastTensorFlowBackendEngineInitialized,
    lessonSelector: (state) => state.lesson,
    lessonSpacingSelector: (state) => state.playbackControlState.notationSpacing.lessonSpacing,
    loopEnabledSelector: (state) => state.playbackControlState.isLoopEnabled,
    pageCountSelector: (state) => state.playbackControlState.pageCount,
    playbackControlStateSelector: (state) => state.playbackControlState,
    recordingStartTimeInMsSelector: (state) => state.recordingStartTimeInMs,
    metricsSegmentsOpenedCountSelector: (state) => state.metrics.segmentsOpenedCount,
    scoreModalViewModelSelector: (state) => state.scoreModalViewModel,
    scoringStartTimeInMsSelector: (state) => state.scoringStartTimeInMs,
    segmentSelector: (state) => state.segment,
    segmentsSelector: (state) => state.lesson?.segments,
    selectedNotationLayoutSelector: (state) => state.playbackControlState.selectedNotationLayout,
    shouldHideSegmentDescriptionSelector: (state) => state.segment?.shouldHideSegmentDescription,
    showPermissionDeniedModalSelector: (state) => state.showPermissionDeniedModal,
    shouldPlayWhenSSReadySelector: (state) => state.shouldPlayWhenSSReady,
    spacingForActiveLessonSelector: (state) => state.playbackControlState.notationSpacing.spacingForActiveLesson,
    speedSelector: (state) => state.playbackControlState.speed,

    transcriptionProgressPercentageSelector: (state) => state.transcriptionProgressPercentage,
    transcriptionStatusSelector: (state) => state.transcriptionStatus,
  }
});

export const lessonPlayerReducer = lessonPlayerSlice.reducer;
